import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { MessageCircle, Send, Search } from "lucide-react";
import { useAuth } from "../lib/auth";

const ROLE_COL = { FOUNDER: "bg-emerald-100 text-emerald-800", BDOM: "bg-sky-100 text-sky-800", PCWM: "bg-violet-100 text-violet-800", GEHM: "bg-amber-100 text-amber-800", SMCM: "bg-pink-100 text-pink-800", PARTNER: "bg-slate-100 text-slate-700" };

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const same = d.toDateString() === now.toDateString();
  return same ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : d.toLocaleDateString();
}

export default function Chat() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [q, setQ] = useState("");
  const listEnd = useRef(null);

  const loadContacts = useCallback(async () => {
    try { const { data } = await api.get("/chat/contacts"); setContacts(data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load contacts"); }
  }, []);
  useEffect(() => { loadContacts(); }, [loadContacts]);

  const loadMessages = useCallback(async (peerId) => {
    if (!peerId) return;
    try { const { data } = await api.get(`/chat/messages?peer_id=${peerId}`); setMessages(data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }, []);
  useEffect(() => { if (activeId) loadMessages(activeId); }, [activeId, loadMessages]);

  // Poll every 6s for new messages
  useEffect(() => {
    if (!activeId) return;
    const t = setInterval(() => loadMessages(activeId), 6000);
    return () => clearInterval(t);
  }, [activeId, loadMessages]);

  // Auto-scroll on new messages
  useEffect(() => { listEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const text = draft.trim(); if (!text || !activeId) return;
    try {
      await api.post("/chat/messages", { peer_id: activeId, text });
      setDraft(""); await loadMessages(activeId); await loadContacts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
  };

  const active = contacts.find(c => c.id === activeId);
  const filtered = contacts.filter(c => !q || (c.name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-3" data-testid="chat-page">
      <div className="flex items-center gap-2">
        <MessageCircle className="text-brand"/>
        <h1 className="text-2xl font-semibold">Team Chat</h1>
        {user.real_role === "PARTNER" && <span className="text-xs text-[color:var(--aii-muted)]">Chat with Aii Group&apos;s Founder / BDOM</span>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" style={{ minHeight: 520 }}>
        <div className="aii-card p-2 md:col-span-1 flex flex-col" data-testid="chat-contacts">
          <div className="relative mb-2">
            <Search size={13} className="absolute left-2 top-2.5 text-[color:var(--aii-muted)]"/>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search contacts…" className="pl-7 w-full py-1.5 border border-[color:var(--aii-border)] rounded-md text-sm"/>
          </div>
          <div className="overflow-y-auto flex-1" style={{ maxHeight: 500 }}>
            {filtered.length === 0 && <div className="text-xs text-[color:var(--aii-muted)] italic p-3">No contacts.</div>}
            {filtered.map(c => (
              <button key={c.id} data-testid={`chat-c-${c.id}`} onClick={() => setActiveId(c.id)}
                      className={`w-full text-left p-2 rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-start justify-between ${activeId === c.id ? "bg-[color:var(--aii-green-tint)]" : ""}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="font-semibold text-sm truncate">{c.name}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase font-bold ${ROLE_COL[c.role] || "bg-slate-100"}`}>{c.role}</span>
                  </div>
                  {c.last_text && <div className="text-xs text-[color:var(--aii-muted)] truncate">{c.last_text}</div>}
                </div>
                <div className="text-[10px] text-[color:var(--aii-muted)] whitespace-nowrap ml-1">
                  {c.last_at ? fmtTime(c.last_at) : ""}
                  {c.unread > 0 && <div className="mt-0.5 inline-flex bg-brand text-white text-[10px] px-1.5 rounded-full font-bold">{c.unread}</div>}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="aii-card md:col-span-2 flex flex-col" data-testid="chat-thread">
          {!active && <div className="flex-1 flex items-center justify-center text-sm text-[color:var(--aii-muted)]">Select a contact to start chatting.</div>}
          {active && (
            <>
              <div className="p-3 border-b border-[color:var(--aii-border)] flex items-center gap-2">
                <div className="font-semibold">{active.name}</div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-bold ${ROLE_COL[active.role] || "bg-slate-100"}`}>{active.role}</span>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ maxHeight: 460 }}>
                {messages.length === 0 && <div className="text-xs italic text-[color:var(--aii-muted)] text-center py-6">Say hello 👋</div>}
                {messages.map(m => {
                  const mine = m.from_user_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                      <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${mine ? "bg-brand text-white rounded-br-sm" : "bg-[color:var(--aii-bg)] text-[color:var(--aii-text)] rounded-bl-sm"}`}>
                        {m.text}
                        <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-[color:var(--aii-muted)]"}`}>{fmtTime(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={listEnd}/>
              </div>
              <div className="p-2 border-t border-[color:var(--aii-border)] flex gap-2">
                <input data-testid="chat-input" value={draft} onChange={e => setDraft(e.target.value)}
                       onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                       placeholder="Type a message… (Enter to send)"
                       className="flex-1 px-3 py-2 border border-[color:var(--aii-border)] rounded-md text-sm"/>
                <button data-testid="chat-send" onClick={send} disabled={!draft.trim()}
                        className="px-4 py-2 bg-brand text-white rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1"><Send size={13}/> Send</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
